import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

const THEMES = ['dark', 'light', 'midnight', 'discord'];

export const THEME_LABELS = {
  dark:     { label: 'Dark',     icon: '🌑' },
  light:    { label: 'Light',    icon: '☀️' },
  midnight: { label: 'Midnight', icon: '🌌' },
  discord:  { label: 'Discord',  icon: '🎮' },
};

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('bm-theme');
    return THEMES.includes(saved) ? saved : getSystemTheme();
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('bm-theme', theme);
  }, [theme]);

  // Respect system preference changes when no manual choice was saved recently
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const saved = localStorage.getItem('bm-theme');
      if (!saved) setThemeState(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setTheme = (t) => {
    if (THEMES.includes(t)) setThemeState(t);
  };

  const cycleTheme = () => {
    setThemeState(prev => THEMES[(THEMES.indexOf(prev) + 1) % THEMES.length]);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, cycleTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
